import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/medicalHistory/page.module.scss';
import { actionTypeOfMedicalHistory } from 'reducer/actionType';
import {
  LOCAL_STORAGE_MH_KEY,
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { ExternalDosespotPharmacyIdDTO } from 'types/patients';

import Spinner from 'components/spinner/Spinner';
import {
  MedicationsLocalStorageType,
  OptionTypeForAllergic,
  OptionTypeForMedications,
} from 'components/medicalHistory/types/types';
import {
  MedicationBaseDTO,
  NonDrugAllergyDTO,
  PatientAllergyDTO,
  PatientPharmacyAllergyMedicationRequestDTO,
} from 'artifacts/dtos';
import { NullableCommunique } from 'types/types';
import Typography from '@mui/material/Typography';
import { useRouter } from 'next/router';
import MetaTag from 'components/meta';
import {
  clearLocalStorage,
  parseErrorMessage,
  removeItemFromLocalStorage,
} from 'utils/helper';
import { ModuleTypes } from 'types/appState';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import axios from 'axios';
import { useAddPatientPharmacyAllergyAndMedication } from 'hooks/useApi';
import useIncompleteTempoRedirect from 'hooks/useIncompleteTempoRedirect';
import { communiqueAction } from 'reduxStateSlice/communique';
import { appointmentIntakeStatusAction } from 'reduxStateSlice/appointment';

function PageMedicalHistoryComplete() {
  const router = useRouter();
  const [state] = usePersistMHReducer();
  const reduxDispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    mutateAsync: addPatientPharmacyAllergyAndMedication,
  } = useAddPatientPharmacyAllergyAndMedication();

  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;
  useIncompleteTempoRedirect(
    !isLoading
    && errorMessage === ''
    && !communiqueData?.sendForms?.consentForm,
  );

  const addMedicalHistory = async () => {
    const pharmacyInformation = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.PHARMACY_INFORMATION,
    );
    const medications = state.find(
      (e: MedicationsLocalStorageType) => e.name === actionTypeOfMedicalHistory.MEDICATIONS,
    );

    const medicationPayload: MedicationBaseDTO[] = medications?.value?.map(
      (item: OptionTypeForMedications) => ({
        displayName: item.value,
        dispensableDrugId: item.dispensableDrugId ?? null,
        routedDoseFormDrugId: item.routedDoseFormDrugId ?? null,
      }),
    ) ?? [];

    const allergies = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.ALLERGIES,
    );
    const drugAllergiesPayload: PatientAllergyDTO[] = allergies?.value?.drugAllergic?.map(
      (item: OptionTypeForAllergic) => ({
        displayName: item.value ?? null,
        allergenId: item.allergenId ?? null,
        reaction: item.reaction ?? null,
        reactionType: item.reactionType as any,
      }),
    ) ?? [];

    const noKnownDrugAllergies = allergies?.value?.noKnownDrugAllergies;

    const nonDrugAllergiesPayload: NonDrugAllergyDTO[] = allergies?.value?.nonDrugAllergic?.map(
      (item: OptionTypeForAllergic) => ({
        source: item.value ?? null,
        reaction: item.reaction ?? null,
      }),
    ) ?? [];

    const pharmacyPayload: ExternalDosespotPharmacyIdDTO = {
      pharmacyId: pharmacyInformation?.value?.selectedPharmacy?.pharmacyId,
    };

    const payload: PatientPharmacyAllergyMedicationRequestDTO = {
      patientId: communiqueData?.patientId ?? 0,
      appointmentId: communiqueData?.appointmentId ?? 0,
      medications: medicationPayload,
      drugAllergies: drugAllergiesPayload,
      nonDrugAllergies: nonDrugAllergiesPayload,
      noKnownDrugAllergies: noKnownDrugAllergies ?? false,
      dosespotPharmacy: pharmacyPayload,
    };

    try {
      await addPatientPharmacyAllergyAndMedication(payload);
      removeItemFromLocalStorage(LOCAL_STORAGE_MH_KEY);
      if (communiqueData) {
        reduxDispatch(communiqueAction({
          ...communiqueData,
          sendForms: {
            ...communiqueData.sendForms,
            pharmacyAllergyMedicationsSubmitted: false,
          },
        }));
      }
      reduxDispatch(appointmentIntakeStatusAction({
        pharmacyAllergyMedicationsSubmitted: true,
      }));
      if (communiqueData?.sendForms?.consentForm) {
        reduxDispatch(
          appStateSettingsAction({
            name: ModuleTypes.consentForm,
            page: null,
          }),
        );
      } else {
        clearLocalStorage();
      }
      setIsLoading(false);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`${parseErrorMessage(error)}`);
      } else {
        setErrorMessage('Something went wrong on AddPatientPharmacyAllergyAndMedication');
        console.error(
          '🚀 ~ error on Submitting AddPatientPharmacyAllergyAndMedication : ',
          error,
        );
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    addMedicalHistory();
  }, []);

  const title = useMemo(() => {
    let message;
    if (isLoading) {
      message = 'Pharmacy & Medications...';
    } else if (errorMessage === '') {
      message = 'The submission has been successful.';
    } else {
      message = errorMessage;
    }
    return message;
  }, [isLoading, errorMessage]);

  return (
    <>
      <MetaTag
        title={title}
      />
      <Box pb={1}>
        <Box
          minHeight="70vh"
          mt={2}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {!isLoading && errorMessage === '' ? (
            <>
              <Typography fontSize="40px">Congratulations!</Typography>
              <p className={styles.titleTextSubText}>
                The submission has been successful.
              </p>
            </>
          ) : (
            isLoading && <Spinner />
          )}
          {errorMessage && (
            <p className="error-message">{errorMessage}</p>
          )}
        </Box>
      </Box>
    </>
  );
}

export default PageMedicalHistoryComplete;
